// #271 Teil 2: Fragen an den Inhaber per WhatsApp — Typen und Aufrufe der
// API `/api/v1/inhaber-fragen` (Backend: services/whatsapp_inhaber_fragen.py).
// Fuer jeden Betrieb gleich; der Name des Inhabers kommt aus den Team-Rufnummern.

// Meta: Zeile einer Auswahlliste 24 Zeichen, Antwort-Knopf 20, hoechstens 3 Knoepfe je Nachricht.
// Paul 04.11.2026: bis zu 5 Antworten — mehr als 3 oder eine ueber 20 Zeichen kommen als Liste.
export const KNOPF_MAX = 24
export const KNOPF_KURZ = 20
export const KNOEPFE_MAX = 3
export const MAX_ANTWORTEN = 5

/** Kommt die Frage als Auswahlliste statt mit Knöpfen? (gleiche Regel wie im Backend) */
export function alsListe(antworten: string[]): boolean {
  const voll = antworten.map((a) => a.trim()).filter(Boolean)
  return voll.length > KNOEPFE_MAX || voll.some((a) => a.length > KNOPF_KURZ)
}

export type InhaberFrage = {
  id: number
  task_id: number | null
  vorgang: string
  frage: string
  antworten: string[]
  kontext: string
  status: "warteschlange" | "gesendet" | "beantwortet" | "erledigt"
  position: number
  angekuendigt: boolean
  sendungen: number
  lieber_anrufen: boolean
  gesendet_am: string | null
  tage_offen: number | null
  antwort_text: string | null
  antwort_art: "knopf" | "text" | "sprache" | null
  beantwortet_am: string | null
  logbuch_ok: boolean | null
  /** Meta-Status der gesendeten Nachricht: sent | delivered | read | failed */
  zustellung?: string | null
  /** Gewählter Empfänger (leer = erster Inhaber) und sein Name */
  an_telefon?: string | null
  an_name?: string | null
  /** Eigener Sendezeitpunkt (ISO); leer = nächster Versand werktags 08:00 */
  senden_ab?: string | null
}

export type InhaberFragenStand = {
  items: InhaberFrage[]
  aktiv: boolean
  inhaber: string | null
  /** Alle Inhaber des Betriebs (Paul 04.11.2026: bei mehreren wählen, an wen es geht) */
  inhaber_liste?: { name: string; telefon: string }[]
  naechster_versand: string
  /** Uhrzeit des automatischen Versands, „HH:MM“, werktags (Paul 04.11.2026: einfach änderbar) */
  versand_uhrzeit?: string
}

export type FrageEntwurf = { frage: string; antworten: string[]; kontext: string; ki?: boolean; an?: string; senden_ab?: string | null }

async function json<T>(res: Response): Promise<T> {
  const daten = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error((daten as { detail?: string }).detail || `HTTP ${res.status}`)
  return daten as T
}

const kopf = { "Content-Type": "application/json" }

export async function fragenLaden(tenant: string): Promise<InhaberFragenStand> {
  return json(await fetch(`/api/v1/inhaber-fragen?tenant=${encodeURIComponent(tenant)}&mit_gesehenen=true`, { credentials: "include" }))
}

/** Neue, noch nicht gesehene Antworten — für den Zähler am Menüpunkt Aufgaben. */
export async function neueAntwortenLaden(): Promise<{ neue_antworten: number }> {
  return json(await fetch("/api/v1/inhaber-fragen/zaehler", { credentials: "include" }))
}

export async function vorschlagLaden(taskId: number): Promise<FrageEntwurf> {
  return json(await fetch("/api/v1/inhaber-fragen/vorschlag", {
    method: "POST", credentials: "include", headers: kopf, body: JSON.stringify({ task_id: taskId }),
  }))
}

/** Mit Aufgabe (`taskId`) oder frei (`tenant` + optionales `thema`, Paul 03.11.2026). */
export async function frageAnlegen(ziel: { taskId: number } | { tenant: string; thema?: string }, e: FrageEntwurf): Promise<InhaberFrage> {
  const wo = "taskId" in ziel ? { task_id: ziel.taskId } : { tenant: ziel.tenant, thema: ziel.thema || null }
  return json(await fetch("/api/v1/inhaber-fragen", {
    method: "POST", credentials: "include", headers: kopf,
    body: JSON.stringify({ ...wo, frage: e.frage, antworten: e.antworten, kontext: e.kontext || null, an: e.an || null, senden_ab: e.senden_ab ?? null }),
  }))
}

export async function frageAendern(id: number, aenderung: Partial<FrageEntwurf> & { position?: number; status?: "zurueckgezogen" | "erledigt" }): Promise<InhaberFrage> {
  return json(await fetch(`/api/v1/inhaber-fragen/${id}`, {
    method: "PATCH", credentials: "include", headers: kopf, body: JSON.stringify(aenderung),
  }))
}

/** „Jetzt senden“: Warteschlange und offene Fragen sofort per WhatsApp (zusaetzlich zum Morgenversand). */
export async function jetztSenden(tenant: string): Promise<{ ergebnis: string }> {
  return json(await fetch("/api/v1/inhaber-fragen/senden", {
    method: "POST", credentials: "include", headers: kopf, body: JSON.stringify({ tenant }),
  }))
}

export async function versandzeitAendern(tenant: string, uhrzeit: string): Promise<{ versand_uhrzeit: string; naechster_versand: string }> {
  return json(await fetch("/api/v1/inhaber-fragen/versandzeit", {
    method: "PUT", credentials: "include", headers: kopf, body: JSON.stringify({ tenant, uhrzeit }),
  }))
}

/** Knoepfe vor dem Speichern: leere raus, Grenzen von WhatsApp. null = alles gut, sonst der Grund. */
export function antwortenFehler(antworten: string[]): string | null {
  const voll = antworten.map((a) => a.trim()).filter(Boolean)
  if (voll.length > MAX_ANTWORTEN) return `Höchstens ${MAX_ANTWORTEN} Antworten`
  const lang = voll.find((a) => a.length > KNOPF_MAX)
  if (lang) return `„${lang}“ ist zu lang (höchstens ${KNOPF_MAX} Zeichen)`
  if (new Set(voll.map((a) => a.toLowerCase())).size !== voll.length) return "Zwei Antworten sind gleich"
  return null
}

/** „Mi 04.11. 07:00“ */
export function versandText(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ""
  const tag = d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })
  return `${tag} ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`
}

